import React, { Fragment, useContext, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Modal } from '@/components/shared/modal';
import { bem } from '@/utilities';
import { useCreateDirectScheduleInquiryMutation } from '@/hooks/apis/directScheduleInquiries';
import { FlashContext, InquiryContext } from '@/providers';
import ic_speech from '@/assets/images/icons/speech.svg';
import ic_close from '@/assets/images/icons/close.svg';
import { useDirectScheduleAdjustmentQuery } from '@/hooks/apis';

const categoryOptions = [
  {
    key: 'usage',
    label: '使い方について'
  },
  {
    key: 'schedule',
    label: '日程について'
  },
  {
    key: 'workplace',
    label: '作業場所について'
  },
  {
    key: 'same_day_cancel',
    label: '当日キャンセルについて'
  },
  {
    key: 'completed_order',
    label: '作業済みの依頼について'
  },
  {
    key: 'expiration',
    label: '有効期限切れについて'
  },
  {
    key: 'other',
    label: 'その他'
  }
];

interface InquiryFormData {
  category: string;
  message: string;
  name: string;
  contactDetails: string;
  blanketContractOrderIds: number[];
}

export const Inquiry: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const { setFlash } = useContext(FlashContext);
  const { disabled } = useContext(InquiryContext);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formData, setFormData] = useState<InquiryFormData>({
    category: '',
    message: '',
    name: '',
    contactDetails: '',
    blanketContractOrderIds: []
  });

  const createInquiryMutation = useCreateDirectScheduleInquiryMutation(token || '');
  const directScheduleAdjustmentQuery = useDirectScheduleAdjustmentQuery(token);
  const orders = directScheduleAdjustmentQuery.data?.orders || [];

  const handleOpenModal = () => {
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!token) {
      alert('エラーが発生しました。ページを再読み込みしてください。');
      return;
    }

    try {
      await createInquiryMutation.mutateAsync({
        category: formData.category,
        message: formData.message,
        name: formData.name,
        contactDetails: formData.contactDetails,
        blanketContractOrderIds: formData.blanketContractOrderIds
      });

      setFlash({
        type: 'success',
        message: <>お問い合わせ内容を送信しました。</>
      });
      setFormData({
        category: 'other',
        message: '',
        name: '',
        contactDetails: '',
        blanketContractOrderIds: []
      });
      handleCloseModal();
    } catch (error) {
      setFlash({
        type: 'error',
        message: <>送信に失敗しました。もう一度お試しください。</>
      });
      console.error('問い合わせ送信エラー:', error);
    }
  };

  return (
    <>
      {/* Floating問い合わせボタン */}
      <button
        className={bem('inquiry', 'button')}
        onClick={handleOpenModal}
        type='button'
        style={{ display: disabled ? 'none' : undefined }}
      >
        <div className={bem('inquiry', 'button-inner')}>
          <img src={ic_speech} style={{ height: 24, width: 24 }} />
          <span className={bem('inquiry', 'button-text')}>お問い合わせ</span>
        </div>
      </button>

      {/* 問い合わせフォームモーダル */}
      <Modal isOpen={isModalOpen} onClickOverlay={handleCloseModal} overrideClassName={bem('inquiry-modal', 'content')}>
        <div className={bem('inquiry', 'modal')}>
          <div className={bem('inquiry', 'modal-header')}>
            <h2 className={bem('inquiry', 'modal-title')}>お問い合わせ</h2>
            <button className={bem('inquiry', 'close-button')} onClick={handleCloseModal} type='button'>
              <img src={ic_close} />
            </button>
          </div>

          <form onSubmit={handleSubmit} className={bem('inquiry', 'form')}>
            <div className={bem('inquiry', 'form-group')}>
              <label htmlFor='message' className={bem('inquiry', 'label')}>
                車両を選択 <span className={bem('inquiry', 'required')}>*</span>
              </label>

              {orders.map((order) => {
                const checked = formData.blanketContractOrderIds.includes(order.id);
                return (
                  <div key={order.id} style={{ display: 'flex', alignItems: 'center' }}>
                    <input
                      type='checkbox'
                      id='order-request-modal-place-checkbox'
                      className={bem('order-request-modal', 'place-checkbox')}
                      onChange={() => {
                        setFormData((prev) => {
                          const newIds = checked
                            ? prev.blanketContractOrderIds.filter((id) => id !== order.id)
                            : [...prev.blanketContractOrderIds, order.id];
                          return { ...prev, blanketContractOrderIds: newIds };
                        });
                      }}
                      checked={checked}
                    />
                    <label
                      htmlFor='order-request-modal-place-checkbox'
                      className={bem('order-request-modal', 'place-label')}
                    >
                      {order.ownedCar.carNumber}
                    </label>
                  </div>
                );
              })}
            </div>

            <div className={bem('inquiry', 'form-group')}>
              <label htmlFor='message' className={bem('inquiry', 'label')}>
                お名前 <span className={bem('inquiry', 'required')}>（必須）</span>
              </label>
              <input
                type='text'
                id='name'
                name='name'
                value={formData.name}
                onChange={handleInputChange}
                className={bem('inquiry', 'input')}
                placeholder='お名前をご記入ください'
                required
              />
            </div>

            <div className={bem('inquiry', 'form-group')}>
              <label htmlFor='contactDetails' className={bem('inquiry', 'label')}>
                連絡先 <span className={bem('inquiry', 'required')}>（必須）</span>
              </label>
              <input
                type='text'
                id='contactDetails'
                name='contactDetails'
                value={formData.contactDetails}
                onChange={handleInputChange}
                className={bem('inquiry', 'input')}
                placeholder='メールアドレスまたは携帯電話番号をご記入ください'
                required
              />
            </div>

            <div className={bem('inquiry', 'form-group')}>
              <label htmlFor='category' className={bem('inquiry', 'label')}>
                お問い合わせ内容 <span className={bem('inquiry', 'required')}>（必須）</span>
              </label>
              <select
                id='category'
                name='category'
                value={formData.category}
                onChange={handleInputChange}
                className={bem('inquiry', 'input')}
                required
              >
                <option value=''>選択してください</option>
                {categoryOptions.map((option) => (
                  <option key={option.key} value={option.key}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <div className={bem('inquiry', 'form-group')}>
              <label htmlFor='message' className={bem('inquiry', 'label')}>
                本文 <span className={bem('inquiry', 'required')}>（必須）</span>
              </label>
              <textarea
                id='message'
                name='message'
                value={formData.message}
                onChange={handleInputChange}
                className={bem('inquiry', 'textarea')}
                rows={5}
                placeholder='入力してください'
                required
              />
            </div>

            <div className={bem('inquiry', 'form-actions')}>
              <button
                type='submit'
                className={bem('inquiry', 'submit-button')}
                disabled={createInquiryMutation.isLoading}
              >
                送信
              </button>
            </div>
          </form>
        </div>
      </Modal>
    </>
  );
};
